import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

function getPreferredLocale(): string | undefined {
  if (typeof navigator === "undefined") return undefined;
  return navigator.language;
}

const MS_PER_DAY = 86_400_000;

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Parses timestamps from the RivetAI API / MySQL `DATETIME`.
 *
 * Values are stored in UTC (`timezone: Z` on the DB driver), but JSON often arrives as
 * `"YYYY-MM-DD HH:mm:ss"` or ISO without `Z`. `new Date(...)` then treats that as **local**
 * wall time and shifts the instant by the user's UTC offset (e.g. always "~7 hours ago").
 *
 * Strings that already include `Z` or a numeric offset are left to the native parser.
 */
export function parseInstantFromApi(dateInput: string | Date): Date {
  if (dateInput instanceof Date) {
    return dateInput;
  }
  const s = String(dateInput).trim();
  if (!s) {
    return new Date(NaN);
  }
  if (/[zZ]$/.test(s)) {
    return new Date(s);
  }
  if (/[+-]\d{2}:?\d{2}$/.test(s)) {
    return new Date(s);
  }
  const isoNoTz = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?$/;
  const mIso = s.match(isoNoTz);
  if (mIso) {
    return new Date(`${mIso[1]}T${mIso[2]}${mIso[3] ?? ""}Z`);
  }
  const mysqlUtc = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(\.\d+)?$/;
  const mSql = s.match(mysqlUtc);
  if (mSql) {
    return new Date(`${mSql[1]}T${mSql[2]}${mSql[3] ?? ""}Z`);
  }
  return new Date(s);
}

/**
 * Activity feed style: browser-locale relative time (e.g. "7 hours ago", "yesterday")
 * or a medium date for older items. Uses {@link Intl.RelativeTimeFormat} and
 * {@link Intl.DateTimeFormat} with the user’s preferred language.
 */
export function formatRelativeActivityDate(dateInput: string | Date): string {
  const date = parseInstantFromApi(dateInput);
  const locale = getPreferredLocale();
  const now = new Date();
  const startToday = startOfLocalDay(now);
  const startEvent = startOfLocalDay(date);
  const calendarDayDiff = Math.round((startToday.getTime() - startEvent.getTime()) / MS_PER_DAY);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (calendarDayDiff === 1) {
    return rtf.format(-1, "day");
  }

  if (calendarDayDiff === 0) {
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) {
      return new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(date);
    }
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    if (diffSec < 60) return rtf.format(-diffSec, "second");
    if (diffMin < 60) return rtf.format(-diffMin, "minute");
    return rtf.format(-diffHour, "hour");
  }

  if (calendarDayDiff >= 2 && calendarDayDiff <= 30) {
    return rtf.format(-calendarDayDiff, "day");
  }

  if (calendarDayDiff > 30) {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  }

  if (calendarDayDiff < 0) {
    const futureDays = -calendarDayDiff;
    if (futureDays <= 7) {
      return rtf.format(futureDays, "day");
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  }

  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

/**
 * Date + time for UI display using the viewer's locale and timezone.
 * Parses RivetAI API / MySQL UTC timestamps via {@link parseInstantFromApi}.
 */
export function formatLocalizedDateTime(dateInput: string | Date | null | undefined): string {
  if (dateInput === null || dateInput === undefined || dateInput === "") {
    return "";
  }
  const date = parseInstantFromApi(dateInput);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(getPreferredLocale(), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/** Time-of-day only, viewer locale and timezone ({@link parseInstantFromApi}). */
export function formatLocalizedTime(dateInput: string | Date | null | undefined): string {
  if (dateInput === null || dateInput === undefined || dateInput === "") {
    return "";
  }
  const date = parseInstantFromApi(dateInput);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(getPreferredLocale(), { timeStyle: "short" }).format(date);
}

/**
 * Parses a calendar date (YYYY-MM-DD or legacy ISO) at local noon so the day
 * does not drift across timezones.
 */
function calendarDateLocalNoon(dateInput: string | Date | null | undefined): Date | null {
  if (dateInput === null || dateInput === undefined || dateInput === "") {
    return null;
  }
  if (typeof dateInput === "string") {
    const dateOnly = dateInput.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnly) {
      const [year, month, day] = dateOnly[1].split("-").map(Number);
      return new Date(year, month - 1, day, 12, 0, 0, 0);
    }
  }
  if (dateInput instanceof Date) {
    if (Number.isNaN(dateInput.getTime())) return null;
    return new Date(
      dateInput.getFullYear(),
      dateInput.getMonth(),
      dateInput.getDate(),
      12,
      0,
      0,
      0
    );
  }
  return null;
}

/**
 * Calendar date without clock time (shoot days, YYYY-MM-DD fields).
 * Uses local noon so the displayed day does not drift across timezones.
 */
export function formatCalendarDateMedium(dateInput: string | Date | null | undefined): string {
  const localNoon = calendarDateLocalNoon(dateInput);
  if (localNoon) {
    return new Intl.DateTimeFormat(getPreferredLocale(), { dateStyle: "medium" }).format(localNoon);
  }
  if (dateInput === null || dateInput === undefined || dateInput === "") {
    return "";
  }
  const localized = formatLocalizedLongDate(dateInput);
  return localized ?? "";
}

/** Short weekday for compact calendar headers (e.g. DooD day columns). */
export function formatCalendarWeekdayShort(dateInput: string | Date | null | undefined): string {
  const localNoon = calendarDateLocalNoon(dateInput);
  if (!localNoon) {
    return "";
  }
  return new Intl.DateTimeFormat(getPreferredLocale(), { weekday: "short" }).format(localNoon);
}

/** Short month and day without the year for narrow calendar columns (e.g. printed DooD). */
export function formatCalendarMonthDayShort(dateInput: string | Date | null | undefined): string {
  const localNoon = calendarDateLocalNoon(dateInput);
  if (!localNoon) {
    return "";
  }
  return new Intl.DateTimeFormat(getPreferredLocale(), { month: "short", day: "numeric" }).format(
    localNoon
  );
}

/** Long month name for calendar month group headers (e.g. DooD). */
export function formatCalendarMonthLong(dateInput: string | Date | null | undefined): string {
  const localNoon = calendarDateLocalNoon(dateInput);
  if (!localNoon) {
    return "";
  }
  return new Intl.DateTimeFormat(getPreferredLocale(), { month: "long" }).format(localNoon);
}

/** Stable YYYY-MM key for grouping calendar dates by month. */
export function calendarYearMonthKey(dateInput: string | Date | null | undefined): string {
  const localNoon = calendarDateLocalNoon(dateInput);
  if (!localNoon) {
    return "";
  }
  const year = localNoon.getFullYear();
  const month = String(localNoon.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/** Long calendar date without clock time ({@link formatCalendarDateMedium}). */
export function formatCalendarDateLong(dateInput: string | Date | null | undefined): string {
  const localNoon = calendarDateLocalNoon(dateInput);
  if (localNoon) {
    return new Intl.DateTimeFormat(getPreferredLocale(), {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(localNoon);
  }
  if (dateInput === null || dateInput === undefined || dateInput === "") {
    return "";
  }
  const localized = formatLocalizedLongDate(dateInput);
  return localized ?? "";
}

/**
 * Notes-style updated label: relative day + localized time, or medium date when older.
 */
export function formatNoteUpdatedDate(dateInput: string | Date | null | undefined): string {
  if (dateInput === null || dateInput === undefined || dateInput === "") {
    return "";
  }
  const date = parseInstantFromApi(dateInput);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const locale = getPreferredLocale();
  const now = new Date();
  const dayDiff = Math.round(
    (startOfLocalDay(now).getTime() - startOfLocalDay(date).getTime()) / MS_PER_DAY
  );
  const time = new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(date);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (dayDiff === 0) {
    return `${rtf.format(0, "day")} ${time}`;
  }
  if (dayDiff === 1) {
    return `${rtf.format(-1, "day")} ${time}`;
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

/**
 * Full date + time for tooltips (`title`) using the user’s locale and timezone.
 */
export function formatFullTimestampForTooltip(dateInput: string | Date): string {
  return formatLocalizedDateTime(dateInput);
}

/** Long catalog "as of" date — UTC calendar day, no clock time ({@link parseInstantFromApi}). */
export function formatCatalogAsOfDate(dateInput: string | Date): string | null {
  const date = parseInstantFromApi(dateInput);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getPreferredLocale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** Long calendar date in the viewer's locale and timezone ({@link parseInstantFromApi}). */
export function formatLocalizedLongDate(dateInput: string | Date): string | null {
  const date = parseInstantFromApi(dateInput);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getPreferredLocale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

/**
 * Formats a date/time for display in a user-friendly way, avoiding redundancy.
 *
 * Rules:
 * - Today: "Today • 7:01 AM" (or relative time when includeTime is false)
 * - Yesterday: "Yesterday • 7:01 AM" (or "Yesterday" when includeTime is false)
 * - Within last 30 days: Shows relative time (e.g., "2 days ago") + time if includeTime is true
 *   - fromNow() automatically shows: "a few seconds ago", "15 minutes ago", "2 hours ago" (over 59 min), "a day ago" (over 23 hours), "2 days ago"
 * - Older than 30 days: "Dec 15, 2025 • 7:01 AM" (date only, no relative time)
 *
 * Note: When includeTime is false (members table), it shows relative time for today:
 * - 0-59 seconds: "a few seconds ago"
 * - 1-59 minutes: "15 minutes ago"
 * - 1-23 hours (same day): "2 hours ago"
 * - Previous calendar day: "Yesterday"
 * - 2+ days: "2 days ago"
 *
 * @param date - Date string or Date object to format
 * @param includeTime - Whether to include the time (default: true)
 * @returns Formatted date/time string
 */
export const formatLoginDate = (date: string | Date, includeTime: boolean = true): string => {
  const instant = parseInstantFromApi(date);
  if (Number.isNaN(instant.getTime())) {
    return "";
  }
  const locale = getPreferredLocale();
  const dateObj = dayjs(instant);
  const formattedTime = new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(instant);
  const formattedDate = new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(instant);
  const isToday = dateObj.isSame(dayjs(), "day");
  const isYesterday = dateObj.isSame(dayjs().subtract(1, "day"), "day");
  const daysDiff = dayjs().diff(dateObj, "day");

  if (isYesterday) {
    const yesterdayLabel = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      -1,
      "day"
    );
    return includeTime ? `${yesterdayLabel} • ${formattedTime}` : yesterdayLabel;
  }

  if (isToday) {
    if (!includeTime) {
      return dateObj.fromNow();
    }
    const todayLabel = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(0, "day");
    return `${todayLabel} • ${formattedTime}`;
  }

  if (daysDiff <= 30) {
    const relativeTimeStr = dateObj.fromNow();
    return includeTime ? `${relativeTimeStr} • ${formattedTime}` : relativeTimeStr;
  }

  return includeTime ? `${formattedDate} • ${formattedTime}` : formattedDate;
};

/**
 * Full date + time for tooltips (`title`). Prefer over hardcoded dayjs patterns.
 */
export const formatFullDateTime = (date: string | Date): string => {
  return formatLocalizedDateTime(date);
};
